import PropTypes from "prop-types";
import {
    useState,
    useEffect,
    useCallback,
    useRef,
    useLayoutEffect,
} from "react";
import { usePathname } from "next/navigation";
import { useActiveLink } from "./useActiveLink";
import NavItem from "./navItem";

// ----------------------------------------------------------------------

const subHeadingConfig = {
    itemGap: 1,
    iconSize: 24,
    currentRole: "admin",
    itemRootHeight: 44,
    itemSubHeight: 36,
    itemPadding: "0px 8px 0px 12px",
    itemRadius: 8,
    hiddenLabel: false,
};

export default function NavList({ data, depth, hasChild, config }) {
    const pathname = usePathname();
    const active = useActiveLink(data.path, hasChild);
    const externalLink = data.path.includes("http");
    const [open, setOpen] = useState(active);

    useEffect(() => {
        if (!active) handleClose();
    }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggle = useCallback(() => setOpen((prev) => !prev), []);
    const handleClose = useCallback(() => setOpen(false), []);

    return (
        <>
            <NavItem
                navItems={data}
                depth={depth}
                open={open}
                active={active}
                externalLink={externalLink}
                onClick={handleToggle}
                config={config}
            />

            {hasChild && (
                <Collapse in={open} unmountOnExit>
                    <NavSubList
                        data={data.children}
                        depth={depth}
                        config={subHeadingConfig}
                    />
                </Collapse>
            )}
        </>
    );
}

NavList.propTypes = {
    config: PropTypes.object,
    data: PropTypes.object,
    depth: PropTypes.number,
    hasChild: PropTypes.bool,
};

// ----------------------------------------------------------------------

function NavSubList({ data, depth, config }) {
    return (
        <>
            {data.map((list) => (
                <NavList
                    key={list.title + list.path}
                    data={list}
                    depth={depth + 1}
                    hasChild={!!list.children}
                    config={config}
                />
            ))}
        </>
    );
}

NavSubList.propTypes = {
    config: PropTypes.object,
    data: PropTypes.array,
    depth: PropTypes.number,
};

// ----------------------------------------------------------------------
// Custom Collapse (no MUI) — smooth & reliable
// ----------------------------------------------------------------------

function Collapse({
    in: open,
    children,
    unmountOnExit = true,
    duration = 300,
}) {
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const [rendered, setRendered] = useState(open);
    const firstMount = useRef(true);

    // Ensure the wrapper exists when opening
    useEffect(() => {
        if (open) setRendered(true);
        else if (!open && !unmountOnExit) setRendered(true); // keep it mounted if not unmounting
    }, [open, unmountOnExit]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const content = contentRef.current;
        if (!container || !content) return;

        // On first mount, just set the final state without animating
        if (firstMount.current) {
            firstMount.current = false;
            if (open) {
                container.style.height = "auto";
            } else {
                // if starting closed and unmounting, don't render at all
                if (unmountOnExit) setRendered(false);
                else container.style.height = "0px";
            }
            return;
        }

        // Opening
        if (open) {
            // Start from 0 -> target height
            container.style.display = "block";
            container.style.overflow = "hidden";
            container.style.transition = "none";
            container.style.height = "0px";

            // Force reflow
            // eslint-disable-next-line no-unused-expressions
            container.offsetHeight;

            const target = content.scrollHeight;
            container.style.transition = `height ${duration}ms ease`;
            container.style.height = `${target}px`;

            const onEnd = (e) => {
                if (e.target !== container) return;
                container.removeEventListener("transitionend", onEnd);
                container.style.transition = "none";
                container.style.height = "auto"; // allow natural growth
                container.style.overflow = "visible";
            };
            container.addEventListener("transitionend", onEnd);
        } else {
            // Closing: auto -> pixel -> 0
            container.style.overflow = "hidden";
            container.style.transition = "none";
            container.style.height = `${content.scrollHeight}px`;

            // Force reflow
            // eslint-disable-next-line no-unused-expressions
            container.offsetHeight;

            container.style.transition = `height ${duration}ms ease`;
            container.style.height = "0px";

            const onEnd = (e) => {
                if (e.target !== container) return;
                container.removeEventListener("transitionend", onEnd);
                container.style.transition = "none";
                if (unmountOnExit) {
                    setRendered(false);
                } else {
                    container.style.display = "none";
                }
            };
            container.addEventListener("transitionend", onEnd);
        }
    }, [open, duration, unmountOnExit]);

    if (!rendered) return null;

    return (
        <div
            ref={containerRef}
            style={{
                height: open ? "auto" : "0px",
                overflow: "hidden",
                willChange: "height",
            }}
            aria-hidden={!open}
        >
            <div ref={contentRef}>{children}</div>
        </div>
    );
}

Collapse.propTypes = {
    in: PropTypes.bool.isRequired,
    children: PropTypes.node,
    unmountOnExit: PropTypes.bool,
    duration: PropTypes.number,
};
